import { useMemo, useState, useEffect, useRef, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { PATHURLDOMAIN } from "../../constantes/constantes";
import "./abstract.sass";
import "../skeleton/skeletonLoader.sass";

const RESUME_LIMIT_MOBILE = 200;
const CHARS_PER_FRAME = 10;

/**
 * Trunca texto de forma inteligente sin cortar palabras a la mitad.
 * @param {string} text - Texto a truncar
 * @param {number} limit - Límite de caracteres
 * @returns {string} Texto truncado con "..." si excede el límite
 */
function truncateSmart(text, limit) {
  if (!text) return "";
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 280 ? cut.slice(0, lastSpace) : cut).trim() + "…";
}

function useTypingEffect(target, isActive, resetTrigger) {
  const [displayed, setDisplayed] = useState("");
  const indexRef = useRef(0);
  const rafRef = useRef(null);

  // Reset effect
  useEffect(() => {
    setDisplayed("");
    indexRef.current = 0;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, [resetTrigger]);

  // Main typing effect (consolidates typing logic and fallback)
  useEffect(() => {
    // Handle inactive state (fallback logic consolidated here)
    if (!isActive) {
      setDisplayed(target);
      indexRef.current = target.length;
      return;
    }

    // Handle active typing state
    const tick = () => {
      indexRef.current = Math.min(indexRef.current + CHARS_PER_FRAME, target.length);
      setDisplayed(target.slice(0, indexRef.current));

      if (indexRef.current < target.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    if (indexRef.current < target.length) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, isActive]);

  return displayed;
}

/**
 * TextContent memoizado: ahora recibe `markdown` (string) en vez de `html`
 */
const TextContent = memo(
  ({ markdown, parentBase }) => {
    return (
      <div className="summary-text">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          // Si NO necesitas HTML dentro del markdown, NO uses rehypeRaw.
          // Si necesitas HTML, lo más seguro es: rehypeRaw + rehypeSanitize (ver comentario abajo).
          // rehypePlugins={[rehypeRaw, rehypeSanitize]}
          rehypePlugins={[rehypeSanitize]} // útil si en algún momento habilitas raw
          components={{
            a: ({ href, children, ...props }) => {
              const safeHref = href || "";

              // Mantener tu lógica de navegación cuando venga ?q=
              const isSearchLink = safeHref.includes("?q=") || safeHref.includes("&q=");

              const handleClick = (e) => {
                if (!isSearchLink) return;

                e.preventDefault();
                e.stopPropagation();

                const base = parentBase ? parentBase.replace(/\/+$/, "") : "";
                const query = safeHref.startsWith("?") ? safeHref : "?" + safeHref.split("?").pop();
                const fullUrl = base + "/buscador/" + query;

                try {
                  if (window.top !== window.self) window.top.location.href = fullUrl;
                  else window.location.href = fullUrl;
                } catch {
                  window.parent.postMessage({ type: "navigate", url: fullUrl }, base || "*");
                }
              };

              return (
                <a href={safeHref} target="_top" rel="noreferrer" onClick={handleClick} {...props} >
                  {children}
                </a>
              );
            },
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    );
  },
  (prev, next) => prev.markdown === next.markdown && prev.parentBase === next.parentBase
);

TextContent.displayName = "TextContent";

export default function AbstractComponent({ resume, isMobile, isStreaming = false, query }) {
  const [expanded, setExpanded] = useState(false);
  const [searchId, setSearchId] = useState(0);
  const prevStreamingRef = useRef(false);

  useEffect(() => {
    if (isStreaming && !prevStreamingRef.current) {
      setSearchId((id) => id + 1);
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Truncar solo en mobile
  const isLong = isMobile && (resume?.length ?? 0) > RESUME_LIMIT_MOBILE;

  const shortText = useMemo(
    () => truncateSmart(resume, RESUME_LIMIT_MOBILE),
    [resume]
  );

  const markdownSource = !isMobile || expanded || !isLong ? resume : shortText;

  const typedMarkdown = useTypingEffect(markdownSource || "", isStreaming, searchId);
  const isTyping = isStreaming && typedMarkdown.length < (markdownSource || "").length;

  const parentBase = useMemo(
    () => (PATHURLDOMAIN ? PATHURLDOMAIN.replace(/\/+$/, "") : ""),
    []
  );

  return (
    <>
      {resume && (
        <section className="summary-card" aria-label="Resumen de resultados">
          <div className="line-upsale"></div>
          <div className="dots_border"></div>
          <header className="summary-header">
            <div className="summary-title-wrap">
              <h3 className="summary-title">{query}</h3>
              <span className="summary-icon" aria-hidden="true">
                <svg
                  className="summary-icon-svg"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                  fill="none"
                  stroke="#333333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7 3h10a2 2 0 0 1 2 2v16l-7-4-7 4V5a2 2 0 0 1 2-2z" />
                  <path d="M12 9.2l.35.95.95.35-.95.35-.35.95-.35-.95-.95-.35.95-.35.35-.95z" />
                </svg>
              </span>
            </div>
          </header>

          <div className={`summary-text-wrap ${!expanded && isLong ? "is-truncated" : ""}`}>
            <TextContent markdown={typedMarkdown} parentBase={parentBase} />

            {isTyping && (
              <section className="skeleton-summary-card">
                <div className="skeleton-summary-text-wrap">
                  <div className="skeleton-line skeleton-line-text"></div>
                  <div className="skeleton-line skeleton-line-text"></div>
                  <div className="skeleton-line skeleton-line-text skeleton-line-short"></div>
                </div>
              </section>
            )}

            {(isStreaming || isTyping) && (
              <span
                className="streaming-indicator"
                title="Recibiendo datos en tiempo real"
                aria-label="Escribiendo"
              >
                ...
              </span>
            )}

            {!expanded && isLong && <div className="summary-fade" aria-hidden="true" />}
          </div>

          {isLong && (
            <div className="summary-footer">
              <button
                type="button"
                className="summary-more"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
              >
                {expanded ? "Mostrar menos" : "Mostrar más"}
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}